import React from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '@/styles/Layout';
import { CustomizableHeading } from '@/styles/Text';
import Loading from '@/components/Loading';
import PaginationButton from '@/components/pagination/PaginationButton';
import API_URL from '@/config';
import { CourseType } from '@/models/course';

// Must match COURSES_PER_INDEX_PAGE in server.mjs, so that /courses?page=N shows
// the same courses whether it is rendered by the server or by React.
const COURSES_PER_PAGE = 200;

// Norwegian groups thousands with a space: 10896 -> "10 896".
const groupThousands = (value: number) => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

interface CourseIndexResponse {
  count: number;
  data: CourseType[];
}

const CourseLinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.15rem 2rem;
  @media ${({ theme }) => theme.device.laptop} {
    grid-template-columns: repeat(2, 1fr);
  }
  @media ${({ theme }) => theme.device.tablet} {
    grid-template-columns: 1fr;
  }
`;

const CourseLink = styled(Link)`
  display: block;
  padding: 0.15rem 0;
  color: ${({ theme }) => theme.color.darkBlue};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const CourseCode = styled.span`
  font-family: poppinsmedium;
  font-weight: bold;
`;

const PaginationWrapper = styled.div`
  padding: 3rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const PageGap = styled.span`
  color: ${({ theme }) => theme.color.darkBlue};
`;

const useCourseIndex = (page: number) =>
  useQuery<CourseIndexResponse>(
    ['course-index', page],
    async () => {
      const offset = (page - 1) * COURSES_PER_PAGE;
      const response = await axios.get(`${API_URL}/course/all/?n=${COURSES_PER_PAGE}&offset=${offset}&order_by=course_code&direction=asc`);
      return response.data;
    },
    { keepPreviousData: true },
  );

/**
 * Builds a short list of page numbers around the current one, always including
 * the first and last page so every part of the index stays reachable.
 */
const visiblePages = (page: number, totalPages: number) => {
  const candidates = [1, page - 1, page, page + 1, totalPages];
  return candidates.filter((number, index) => number >= 1 && number <= totalPages && candidates.indexOf(number) === index).sort((a, b) => a - b);
};

const CourseIndexPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const requestedPage = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;

  const { data, isLoading, isError } = useCourseIndex(page);

  if (isLoading) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  if (isError || !data) {
    return (
      <Layout>
        <div style={{ margin: '3rem auto' }}>Noe gikk galt...</div>
      </Layout>
    );
  }

  const totalPages = Math.max(1, Math.ceil(data.count / COURSES_PER_PAGE));
  const pageNumbers = visiblePages(page, totalPages);

  return (
    <Layout>
      <CustomizableHeading headingLevel='h1' option='bold' margin='2rem 0 0.5rem'>
        Alle emner ved NTNU
      </CustomizableHeading>
      <CustomizableHeading headingLevel='h2' option='subtitle' fontWeight='400' margin='0 0 2rem'>
        {groupThousands(data.count)} emner med vurderinger, snittkarakter og strykprosent. Side {page} av {totalPages}.
      </CustomizableHeading>

      <CourseLinkList>
        {data.data.map((course) => (
          <li key={course.course_code}>
            <CourseLink to={`/course/${encodeURIComponent(course.course_code)}`}>
              <CourseCode>{course.course_code}</CourseCode> {course.course_name_norwegian}
            </CourseLink>
          </li>
        ))}
      </CourseLinkList>

      <PaginationWrapper>
        {pageNumbers.map((number, index) => (
          <React.Fragment key={number}>
            {index > 0 && number - pageNumbers[index - 1] > 1 && <PageGap>...</PageGap>}
            <PaginationButton active={number === page} onClick={() => navigate(`/courses?page=${number}`)}>
              {number}
            </PaginationButton>
          </React.Fragment>
        ))}
      </PaginationWrapper>
    </Layout>
  );
};

export default CourseIndexPage;
